<template>
  <div class="space-y-3">
    <!-- Create Card -->
    <div class="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
      <div class="bg-gray-50 border-b border-gray-300 px-4 py-2.5 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-700">Add Faculties</h3>
        <span class="text-xs text-gray-400">Use this batch queue when you need to create several faculties at once.</span>
      </div>

      <div class="p-3 space-y-3">
        <form class="flex gap-2 items-end" @submit.prevent="addFaculty">
          <div class="flex-1 flex flex-col gap-0.5">
            <label class="text-xs font-medium text-gray-600">Faculty Name *</label>
            <InputText
              v-model="facultyName"
              class="w-full"
              placeholder="e.g. Faculty of Engineering"
              :class="{ 'p-invalid': getErrorMessage('faculty_name') }"
              data-testid="faculty-name-input"
            />
            <small v-if="getErrorMessage('faculty_name')" class="text-red-500">
              {{ getErrorMessage('faculty_name') }}
            </small>
          </div>
          <div class="flex flex-col gap-0.5" style="min-width: 10rem">
            <label class="text-xs font-medium text-gray-600">Type *</label>
            <Select
              v-model="facultyType"
              :options="typeOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              data-testid="faculty-type-select"
            />
          </div>
          <Button type="submit" label="Add" icon="pi pi-plus" size="small" :disabled="!facultyName.trim()" data-testid="faculty-add-btn" />
        </form>

        <div v-if="queued.length">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-xs font-medium text-gray-700">
              Queued
              <span class="inline-flex items-center justify-center bg-primary text-white text-[10px] font-bold rounded-full w-4 h-4 ml-1">
                {{ queued.length }}
              </span>
            </span>
            <Button v-if="queued.length > 1" label="Clear All" severity="danger" text size="small" data-testid="faculty-clear-pending-btn" @click="clearItems" />
          </div>

          <DataTable :value="queued" size="small" stripedRows>
            <Column header="#">
              <template #body="{ index }">
                <span class="text-gray-400 font-mono text-xs">{{ index + 1 }}</span>
              </template>
            </Column>
            <Column header="Faculty Name">
              <template #body="{ data }">
                <span class="text-sm font-medium">{{ data.name }}</span>
              </template>
            </Column>
            <Column header="Type">
              <template #body="{ data }">
                <span class="text-xs px-2 py-0.5 rounded-full" :class="data.type === 'academic' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'">
                  {{ data.type === 'academic' ? 'Academic' : 'Non-Teaching' }}
                </span>
              </template>
            </Column>
            <Column v-if="pendingErrors.some(Boolean)" header="Not created">
              <template #body="{ index }">
                <small v-if="pendingErrors[index]" class="text-red-500" :data-testid="`faculty-pending-error-${index}`">
                  {{ pendingErrors[index] }}
                </small>
              </template>
            </Column>
            <Column>
              <template #body="{ index }">
                <Button
                  icon="pi pi-trash"
                  class="p-button-danger p-button-text p-button-rounded p-button-sm"
                  v-tooltip.top="'Remove'"
                  :data-testid="`faculty-remove-pending-${index}`"
                  @click="removeQueued(index)"
                />
              </template>
            </Column>
          </DataTable>
        </div>

        <div class="flex justify-end pt-1.5 border-t border-gray-300">
          <Button
            label="Submit All Faculties"
            icon="pi pi-check"
            size="small"
            severity="success"
            :loading="isSubmitting"
            :disabled="queued.length === 0"
            data-testid="faculty-submit-all-btn"
            @click="submitAll"
          />
        </div>
      </div>
    </div>

    <!-- Existing Faculties Card -->
    <div class="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
      <div class="px-4 py-2.5 border-b border-gray-300 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-700">
          Existing Faculties
          <span v-if="faculties.length" class="text-xs font-normal text-gray-400 ml-1">({{ faculties.length }})</span>
        </h3>
      </div>

      <div class="p-0">
        <div v-if="loading" class="flex items-center justify-center py-8">
          <span class="pi pi-spin pi-spinner text-2xl text-primary"></span>
        </div>

        <DataTable
          v-else-if="faculties.length"
          :value="faculties"
          size="small"
          stripedRows
          :paginator="faculties.length > 10"
          :rows="10"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          :rowsPerPageOptions="[10, 25, 50]"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        >
          <Column field="faculty_name" header="Faculty Name" />
          <Column header="Type">
            <template #body="{ data }">
              <span class="text-xs px-2 py-0.5 rounded-full" :class="data.type === 'non_teaching' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'">
                {{ data.type === 'non_teaching' ? 'Non-Teaching' : 'Academic' }}
              </span>
            </template>
          </Column>
          <Column header="Departments">
            <template #body="{ data }">{{ data.departments?.length || 0 }}</template>
          </Column>
          <Column header="Actions">
            <template #body="{ data }">
              <div class="flex gap-1">
                <Button
                  icon="pi pi-pencil"
                  class="p-button-secondary p-button-text p-button-rounded p-button-sm"
                  v-tooltip.top="'Edit'"
                  :data-testid="`faculty-edit-${data.id}`"
                  @click="openEdit(data)"
                />
                <Button
                  icon="pi pi-trash"
                  class="p-button-danger p-button-text p-button-rounded p-button-sm"
                  v-tooltip.top="'Delete'"
                  :loading="deletingId === data.id"
                  :data-testid="`faculty-delete-${data.id}`"
                  @click="confirmDelete(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>

        <div v-else class="py-6 text-center text-gray-400 text-xs">No faculties created yet</div>
      </div>
    </div>

    <!-- Edit Dialog -->
    <Dialog v-model:visible="showEdit" modal header="Edit Faculty" :style="{ width: '28rem' }">
      <div class="flex flex-col gap-2">
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Faculty Name</label>
          <InputText
            v-model="editName"
            placeholder="Faculty name"
            :class="{ 'p-invalid': getErrorMessage('faculty_name') }"
            data-testid="faculty-edit-name-input"
          />
          <small v-if="getErrorMessage('faculty_name')" class="text-red-500">
            {{ getErrorMessage('faculty_name') }}
          </small>
        </div>
        <div class="flex flex-col gap-0.5">
          <label class="text-xs font-medium text-gray-700">Type</label>
          <Select
            v-model="editType"
            :options="typeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            data-testid="faculty-edit-type-select"
          />
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times" text size="small" data-testid="faculty-edit-cancel-btn" @click="showEdit = false" />
        <Button label="Save" icon="pi pi-check" size="small" :loading="updatingFaculty" :disabled="!editName.trim()" data-testid="faculty-edit-save-btn" @click="submitEdit" />
      </template>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';
import { useFaculties } from '@/composables/queries/useFaculties';
import { useFormErrors } from '@/composables/useFormErrors';
import { submissionToast, useTertiaryEntityWorkflow } from '@/composables/useTertiaryEntityWorkflow';
import type { FacultyType } from '@/types/composition-functions';

const toast = useToast();
const confirm = useConfirm();
const {
  createTertiaryFaculty,
  updateTertiaryFaculty,
  deleteTertiaryFaculty,
  updatingFaculty,
} = useTertiaryMutations();
const { faculties, loading, refreshFaculties } = useFaculties();
const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

const typeOptions = [
  { label: 'Academic', value: 'academic' },
  { label: 'Non-Teaching', value: 'non_teaching' },
];

// ─── Create batch ────────────────────────────────────────────────
const facultyName = ref('');
const facultyType = ref('academic');
const { pendingItems: queued, pendingErrors, isSubmitting, addItem, removeItem, clearItems, submitPending } = useTertiaryEntityWorkflow<{ name: string; type: string }>();

function addFaculty() {
  const trimmed = facultyName.value.trim();
  if (trimmed) {
    addItem({ name: trimmed, type: facultyType.value });
    facultyName.value = '';
  }
}

function removeQueued(idx: number) {
  removeItem(idx);
}

async function submitAll() {
  clearErrors();
  const result = await submitPending(async(item) => {
    await createTertiaryFaculty({ faculty_name: item.name, type: item.type });
  });

  await refreshFaculties();

  const summary = submissionToast(result, 'faculty(ies)');
  if (summary) toast.add(summary);
}

// ─── Edit ────────────────────────────────────────────────────────
const showEdit = ref(false);
const editingId = ref<string | null>(null);
const editName = ref('');
const editType = ref('academic');

function openEdit(item: FacultyType) {
  clearErrors();
  editingId.value = item.id;
  editName.value = item.faculty_name;
  editType.value = item.type || 'academic';
  showEdit.value = true;
}

async function submitEdit() {
  if (!editingId.value) return;
  clearErrors();
  try {
    const result = await updateTertiaryFaculty(editingId.value, { faculty_name: editName.value.trim(), type: editType.value });
    if (result) {
      showEdit.value = false;
      editingId.value = null;
      refreshFaculties();
    }
  } catch (err) {
    handleServerErrors(err);
  }
}

// ─── Delete ──────────────────────────────────────────────────────
const deletingId = ref<string | null>(null);

function confirmDelete(item: FacultyType) {
  confirm.require({
    message: `Delete "${item.faculty_name}"? Departments under it may be affected.`,
    header: 'Delete Faculty',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: async() => {
      deletingId.value = item.id;
      try {
        const ok = await deleteTertiaryFaculty(item.id);
        if (ok) refreshFaculties();
      } finally {
        deletingId.value = null;
      }
    },
  });
}
</script>
